import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Form,
  Input,
  DatePicker,
  Modal,
  Space,
  Tooltip,
  Popconfirm,
  TablePaginationConfig,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import {
  CreateScheduleDto,
  LocationAssistantShiftScheduleResult,
} from '../../utils/api-client';
import { UseMutateAsyncFunction } from 'react-query';
import {
  usePtAdminSetting,
  useUpdatePtAdminSetting,
} from '../../utils/api-hooks/pt-schedule.hook';
import { find } from 'lodash';

// 初始化 dayjs 週次插件
dayjs.extend(weekOfYear);

const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface PTScheduleComponentProps {
  schedules: LocationAssistantShiftScheduleResult[];
  isLoading: boolean;
  pagination: TablePaginationConfig;
  onCreateSchedule: UseMutateAsyncFunction<
    unknown,
    unknown,
    CreateScheduleDto,
    unknown
  >;
  onUpdateSchedule: (values: {
    id: string;
    name?: string;
    startDate?: string;
    endDate?: string;
    note?: string;
  }) => void;
  onDeleteSchedule: (id: string) => void;
}

export const PTSchedulesComponent: React.FC<PTScheduleComponentProps> = ({
  schedules,
  isLoading,
  pagination,
  onCreateSchedule,
  onUpdateSchedule,
  onDeleteSchedule,
}) => {
  const navigate = useNavigate();
  const [promptForm] = Form.useForm<{
    value: string;
  }>();
  const [form] = Form.useForm<{
    dateRange: [dayjs.Dayjs, dayjs.Dayjs];
    note?: string;
  }>();
  const [editForm] = Form.useForm<{ note?: string }>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentSchedule, setCurrentSchedule] =
    useState<LocationAssistantShiftScheduleResult | null>(null);
  const { data: { data: ptAdminSetting } = {} } = usePtAdminSetting();
  const {
    mutateAsync: onUpdatePtAdminSetting,
    isLoading: isUpdatingPtAdminSetting,
  } = useUpdatePtAdminSetting();

  // 打開創建班表的對話框
  const showModal = () => {
    setIsModalVisible(true);
  };

  // 打開編輯班表的對話框
  const showEditModal = (schedule: LocationAssistantShiftScheduleResult) => {
    setCurrentSchedule(schedule);
    editForm.setFieldsValue({
      note: schedule.note || '',
    });
    setIsEditModalVisible(true);
  };

  // 導航到安排人員頁面
  const navigateToAssignment = (
    schedule: LocationAssistantShiftScheduleResult
  ) => {
    navigate(`/schedules/pt/${schedule.id}/assign`);
  };

  // 處理創建班表的表單提交
  const handleCreate = (values: {
    dateRange: [dayjs.Dayjs, dayjs.Dayjs];
    note?: string;
  }) => {
    const [startDate, endDate] = values.dateRange;

    onCreateSchedule({
      startTime: startDate.startOf('week').toISOString(),
      endTime: endDate.endOf('week').toISOString(),
      note: values.note,
    });

    setIsModalVisible(false);
  };

  // 處理編輯班表的表單提交
  const handleEdit = (values: any) => {
    if (!currentSchedule) return;

    onUpdateSchedule({
      id: currentSchedule.id,
      note: values.note,
    });

    setIsEditModalVisible(false);
    setCurrentSchedule(null);
  };

  // 取消操作
  const handleCancel = () => {
    setIsModalVisible(false);
    setIsEditModalVisible(false);
    setCurrentSchedule(null);
  };

  // 計算週次的輔助函數
  const getWeekNumber = (date: string) => {
    return dayjs(date).week();
  };

  const onUpdatePtAdminPromptButtonClick = () => {
    const initialValues =
      find(ptAdminSetting, { key: 'PT_ADMIN_PROMPT' }) || {};

    Modal.confirm({
      title: '更新 PT 後台班表提文字',
      width: 600,
      icon: null,
      content: (
        <Form preserve={false} form={promptForm} initialValues={initialValues}>
          <Form.Item
            name="value"
            label="PT 後台班表提文字"
            rules={[{ required: true, message: '請輸入 PT 後台班表提文字' }]}
          >
            <Input.TextArea rows={8} />
          </Form.Item>
        </Form>
      ),
      onOk: async () => {
        const values = await promptForm.getFieldsValue();

        // 在這裡處理更新邏輯
        return onUpdatePtAdminSetting({
          key: 'PT_ADMIN_PROMPT',
          value: values.value,
        });
      },
    });
  };

  return (
    <>
      <Space direction="vertical" style={{ width: '100%' }}>
        <div className="flex items-center justify-between">
          <span className="text-2xl font-bold">PT班表管理</span>
          <div className="flex items-center gap-2">
            <Button type="primary" icon={<PlusOutlined />} onClick={showModal}>
              建立新班表
            </Button>
            <Button
              onClick={onUpdatePtAdminPromptButtonClick}
              type="primary"
              icon={<EditOutlined />}
            >
              設定 PT 後台班表提文字
            </Button>
          </div>
        </div>

        <Table
          dataSource={schedules}
          size="small"
          rowKey="id"
          loading={isLoading || isUpdatingPtAdminSetting}
          pagination={pagination}
          columns={[
            {
              align: 'center',
              title: '週次',
              key: 'weekNumber',
              render: (_, record: LocationAssistantShiftScheduleResult) => {
                const startWeek = getWeekNumber(record.startTime);
                const endWeek = getWeekNumber(record.endTime);
                return startWeek === endWeek
                  ? `第${startWeek}週`
                  : `第${startWeek}週 - 第${endWeek}週`;
              },
              width: 120,
            },
            {
              align: 'center',
              title: '起始日期',
              dataIndex: 'startTime',
              render: (text: string) => dayjs(text).format('YYYY-MM-DD'),
              width: 120,
            },
            {
              align: 'center',
              title: '結束日期',
              dataIndex: 'endTime',
              render: (text: string) => dayjs(text).format('YYYY-MM-DD'),
              width: 120,
            },
            {
              title: '備註',
              dataIndex: 'note',
            },
            {
              title: '操作',
              key: 'action',
              width: 100,
              render: (record: LocationAssistantShiftScheduleResult) => (
                <div className="flex items-center gap-2">
                  <Tooltip title="編輯班表">
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => showEditModal(record)}
                      type="text"
                      shape="circle"
                    />
                  </Tooltip>
                  <Tooltip title="安排人員">
                    <Button
                      icon={<CalendarOutlined />}
                      onClick={() => navigateToAssignment(record)}
                      type="text"
                      shape="circle"
                    />
                  </Tooltip>
                  <Popconfirm
                    title="確定要刪除此班表嗎？"
                    onConfirm={() => onDeleteSchedule(record.id)}
                    okText="確定"
                    cancelText="取消"
                  >
                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      type="text"
                      shape="circle"
                    />
                  </Popconfirm>
                </div>
              ),
            },
          ]}
        />
      </Space>

      {/* 創建班表對話框 */}
      <Modal
        title="建立新班表"
        open={isModalVisible}
        onCancel={handleCancel}
        destroyOnClose
        footer={null}
      >
        <Form
          preserve={false}
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="dateRange"
            label="班表期間"
            rules={[{ required: true, message: '請選擇班表期間' }]}
          >
            <RangePicker picker="week" />
          </Form.Item>
          <Form.Item name="note" label="備註">
            <TextArea rows={4} placeholder="可選填班表備註" />
          </Form.Item>
          <Form.Item>
            <Space style={{ float: 'right' }}>
              <Button type="primary" htmlType="submit">
                建立
              </Button>
              <Button onClick={handleCancel}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 編輯班表對話框 */}
      <Modal
        title="編輯班表"
        destroyOnClose
        open={isEditModalVisible}
        onCancel={handleCancel}
        footer={null}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="note" label="備註">
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item>
            <Space style={{ float: 'right' }}>
              <Button type="primary" htmlType="submit">
                更新
              </Button>
              <Button onClick={handleCancel}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
